/**
 * CaseFilesTable — the matter's own paper, as a table.
 *
 * Case files are FILES. The questions asked of them are the questions asked of
 * any file list — what is it, who put it there, when, how big — and a row that
 * folds all of that into one grey "Case file · 240 KB" meta line cannot answer
 * them, let alone be sorted by them. Legal authority keeps `SourceRow`, because
 * there the questions are different (relevance, origin, corpus) and the answer
 * is a ranked list rather than a folder.
 *
 * On the shared `Table`, so it inherits the glass header, column resizing and
 * the selection column the bulk-remove action already drives.
 *
 * UPLOADED BY is best-effort by design: the matter's `user_documents` payload
 * carries no uploader today (the authority buckets do, as
 * `uploaded_by_user_id`). Every shape the backend might send is read, a
 * numeric id is resolved against the org roster, and anything unresolvable
 * renders as the standard muted dash rather than an invented name.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCloudArrowDownLight,
  PiFileTextLight,
  PiTrashLight,
} from 'react-icons/pi';
import Table from '../../../shared/TailwindComponents/Table';
import Spinner from '../../../shared/TailwindComponents/Spinner';
import { formatBytes, formatDateTime } from '../../../../utils/functions';
import { documentUploadedAt, resolveUploader } from '../../../../utils/documentMeta';
import { useOrgMembers } from '../../../../hooks/useOrgMembers';
import { MatterSource } from './sourceModel';

interface Props {
  items: MatterSource[];
  selectedIds: string[];
  selectable: boolean;
  deletingIds: string[];
  evaluatingIds: Set<string>;
  onOpen: (item: MatterSource) => void;
  onSelect: (id: string) => void;
  onSelectAll: () => void;
  onDownload: (item: MatterSource) => void;
  onDelete?: (item: MatterSource) => void;
  /** Rendered by the shared Table's own skeleton. */
  isLoading?: boolean;
}

type SortKey = 'name' | 'uploaded' | 'uploadedBy' | 'size';

const CaseFilesTable: React.FC<Props> = ({
  items,
  selectedIds,
  selectable,
  deletingIds,
  evaluatingIds,
  onOpen,
  onSelect,
  onSelectAll,
  onDownload,
  onDelete,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { members } = useOrgMembers();

  const [sortKey, setSortKey] = useState<SortKey>('uploaded');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const NAME = t('Name');
  const UPLOADED_BY = t('Uploaded by');
  const UPLOADED = t('Uploaded');
  const SIZE = t('Size');

  /*
    `Table` hands its `onSort` the column label already flattened to
    `lower_snake`, and compares `sortBy` against the same flattening — so the
    map this component keeps has to be keyed the same way, or the header arrow
    points at a column that is not the one being sorted.
  */
  const flatten = (column: string) => column.toLowerCase().replace(/\s+/g, '_');

  const COLUMN_KEY: Record<string, SortKey> = {
    [flatten(NAME)]: 'name',
    [flatten(UPLOADED_BY)]: 'uploadedBy',
    [flatten(UPLOADED)]: 'uploaded',
    [flatten(SIZE)]: 'size',
  };

  const KEY_COLUMN: Record<SortKey, string> = {
    name: NAME,
    uploadedBy: UPLOADED_BY,
    uploaded: UPLOADED,
    size: SIZE,
  };

  /** id → display name, so a stored user id can still read as a person. */
  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    members.forEach((member) => map.set(String(member.id), member.name));
    return map;
  }, [members]);

  const uploaderLabel = (item: MatterSource): string => resolveUploader(item, memberNames);

  const sorted = useMemo(() => {
    const direction = sortOrder === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      switch (sortKey) {
        case 'size':
          return ((a.size || 0) - (b.size || 0)) * direction;
        case 'uploaded':
          return documentUploadedAt(a).localeCompare(documentUploadedAt(b)) * direction;
        case 'uploadedBy':
          return uploaderLabel(a).localeCompare(uploaderLabel(b)) * direction;
        default:
          return (a.name || '').localeCompare(b.name || '', undefined, { numeric: true }) * direction;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, sortKey, sortOrder, memberNames]);

  const columns = [NAME, UPLOADED_BY, UPLOADED, SIZE];

  const rows = sorted.map((item) => ({
    id: item.id,
    [NAME]: item,
    [UPLOADED_BY]: uploaderLabel(item),
    [UPLOADED]: documentUploadedAt(item) ? formatDateTime(documentUploadedAt(item)) : '',
    [SIZE]: item.size ? formatBytes(item.size) : '',
    _raw: item,
  }));

  const muted = <span className="text-gray-400 dark:text-gray-500">—</span>;

  const renderCell = (column: string, value: any) => {
    if (column === NAME) {
      const item = value as MatterSource;
      const busy = deletingIds.includes(item.id) || evaluatingIds.has(item.id);
      return (
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full
                           border border-hairline-soft bg-surface-sunken/70 text-fg-muted">
            {busy
              ? <Spinner size="inline" className="!h-3 !w-3" />
              : <PiFileTextLight className="h-3.5 w-3.5" />}
          </span>
          <span className="truncate text-fg-body font-medium" title={item.name}>
            {item.name}
          </span>
        </div>
      );
    }
    // One empty-cell treatment for the three fact columns.
    if (!value) return muted;
    return undefined;
  };

  const actions = [
    {
      label: t('Download'),
      icon: <PiCloudArrowDownLight />,
      onClick: (row: any) => onDownload(row._raw as MatterSource),
    },
    ...(onDelete
      ? [{
        label: t('Remove'),
        icon: <PiTrashLight />,
        isDestructive: true,
        onClick: (row: any) => onDelete(row._raw as MatterSource),
      }]
      : []),
  ];

  return (
    <Table
      tableId="matter-case-files"
      columns={columns}
      data={rows}
      isLoading={isLoading}
      renderCell={renderCell}
      actions={actions}
      enableSelection={selectable}
      selectedIds={selectedIds}
      onSelect={onSelect}
      onSelectAll={onSelectAll}
      onRowClick={(row: any) => onOpen(row._raw as MatterSource)}
      sortableColumns={columns}
      sortBy={flatten(KEY_COLUMN[sortKey])}
      sortOrder={sortOrder}
      onSort={(flattenedColumn, order) => {
        setSortKey(COLUMN_KEY[flattenedColumn] || 'name');
        setSortOrder(order);
      }}
      min="min-h-[16rem]"
      max="max-h-[calc(100vh-360px)]"
      defaultColumnWidths={{ [NAME]: 360, [UPLOADED_BY]: 180, [UPLOADED]: 190, [SIZE]: 110 }}
    />
  );
};

export default CaseFilesTable;
